import Background from "../components/Background";

export default function TermsOfUse() {
  return (
    <Background>
      <div className="min-h-screen px-4 py-10">
        <div className="w-full max-w-4xl mx-auto bg-white rounded-3xl border border-gray-200 shadow-lg p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            AYUPATH – Terms and Conditions of Use
          </h1>
          
          <p className="text-sm text-gray-600 mb-8">
            Effective date: 01/01/2026
          </p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <p>
              These Terms and Conditions of Use ("Terms") govern your access to and use of the AYUPATH 
              websites, mobile applications and other services (collectively, the "Service"). The Service 
              is provided by Power Pilot Pvt. Ltd. ("the Company," "we," "our," or "us"). By accessing or 
              using the Service you agree to be bound by these Terms, which constitute a legally binding 
              agreement between you and the Company. If you do not agree to these Terms, you must not use 
              the Service.
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <p className="mb-3">
                <strong>One-off purchases only.</strong> The preview is free. The full report is a single,
                one-off purchase. We do not offer subscriptions, trials or automatic renewals. See Section 5 for
                full details regarding fees and refunds.
              </p>
              <p>
                <strong>Additional policies.</strong> These Terms incorporate our Privacy Policy by reference. Supplemental policies or terms may apply to particular products 
                or services; those supplemental terms become part of these Terms when posted. We reserve the right 
                to modify these Terms at any time (see Section 14).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Important Disclaimers
              </h2>
              <ul className="list-disc pl-6 space-y-3">
                <li>
                  <strong>Informational and entertainment purposes only.</strong> AYUPATH generates personalised 
                  life readings based on birth details and uses artificial intelligence to analyse your data. The 
                  information provided through the Service is for informational and entertainment purposes. It is 
                  not a substitute for professional medical, psychiatric, legal, financial or investment advice. 
                  You are solely responsible for decisions you make based on the information provided.
                </li>
                <li>
                  <strong>No guarantees.</strong> We do not guarantee that the Service will meet your requirements, 
                  be uninterrupted, timely or error‑free, or that results from the Service will be accurate or 
                  reliable. We do not warrant that any products or services purchased through the Service will 
                  provide any specific benefit.
                </li>
                <li>
                  <strong>Assumption of risk.</strong> You assume all risks associated with your activities inspired 
                  or encouraged by the Service and accept responsibility for your health and well‑being, and that of 
                  your family.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. The Service
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                3.1 Ownership and licence
              </h3>
              <p>
                Except for your User Content (see Section 4), all text, images, graphics, compilations, software 
                and other materials displayed on the Service are proprietary to the Company or our licensors. 
                You are granted a non‑exclusive, non‑transferable, revocable licence to access and use the Service 
                for your personal, non‑commercial purposes. You may not reproduce, modify, decompile, reverse 
                engineer, sell or redistribute any part of the Service except as expressly allowed under these 
                Terms.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                3.2 Modifications and updates
              </h3>
              <p>
                We may change or discontinue the Service, or any feature or portion of it, at any time with or 
                without notice. We are not liable if these changes affect your ability to use the Service or 
                result in the deletion of content.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                3.3 Access and equipment
              </h3>
              <p>
                You are responsible for obtaining and maintaining all devices and services needed to access the 
                Service and for all related charges.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. User Content
              </h2>
              <p>
                "User Content" means the information you submit or upload to the Service, including your registration 
                details, birth details, messages, reviews and other data. You retain ownership of your User Content, 
                but you grant us a non‑exclusive, worldwide, royalty‑free, transferable licence to use, reproduce, 
                distribute, publicly display and perform your User Content in connection with operating and providing 
                the Service.
              </p>
              <p className="mt-4">
                You are solely responsible for your User Content and your interactions with other users. We do not 
                control or endorse User Content and have no obligation to monitor it. You agree that the Company 
                will not be responsible for any loss or damage arising from your interactions with other users.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Fees and Payment
              </h2>
              <p className="mb-4">
                The preview is free. The full report is a one-off purchase. If you buy it, the following applies:
              </p>
              <ul className="list-disc pl-6 space-y-3">
                <li>
                  <strong>Price.</strong> The price is shown in Australian dollars before you pay and is charged 
                  once. There is no subscription, trial or automatic renewal, and nothing recurring.
                </li>
                <li>
                  <strong>Payment.</strong> Payments are processed by Stripe. We do not see or store your card details.
                </li>
                <li>
                  <strong>Delivery.</strong> The full report is written for your chart and delivered by email within 
                  the time stated before purchase. If we cannot deliver it, we will refund you in full.
                </li>
                <li>
                  <strong>14-day satisfaction refund.</strong> If the report is not useful to you, reply to the 
                  delivery email within 14 days of delivery and we will refund you in full. You do not need to give 
                  a reason.
                </li>
                <li>
                  <strong>Your consumer rights.</strong> Our goods and services come with guarantees that cannot be 
                  excluded under the Australian Consumer Law. Nothing in these Terms limits those rights, or any 
                  non-excludable rights you have under the law where you live.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. User Representations and Restrictions
              </h2>
              <p className="mb-4">
                By using the Service, you represent and warrant that:
              </p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>You have the legal capacity to enter into these Terms and are at least 16 years old.</li>
                <li>You will not access the Service via automated or non‑human means (such as bots or scripts).</li>
                <li>You will not use the Service for any illegal or unauthorised purpose.</li>
                <li>You are not located in a country subject to a government embargo or on a sanctions list.</li>
              </ol>
              <p className="mt-4">
                If you provide untrue, inaccurate or incomplete information, or we reasonably believe you are under 
                the age limit or otherwise ineligible, we may suspend or terminate your use of the Service.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                Prohibited Activities
              </h3>
              <p className="mb-3">You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Systematically retrieve data or content from the Service to create a database or directory without our consent.</li>
                <li>Modify, adapt, translate, decompile, reverse engineer or create derivative works from the Service.</li>
                <li>Use the Service for any revenue‑generating or commercial purpose not authorised by us.</li>
                <li>Make the Service available to multiple devices or users simultaneously.</li>
                <li>Circumvent or interfere with security features; upload viruses or other harmful code; or use automated systems such as spiders or scrapers.</li>
                <li>Use the Service to send unsolicited commercial emails or automated queries.</li>
                <li>Disparage, tarnish or otherwise harm the Company or the Service.</li>
                <li>Use the Service in a manner inconsistent with any applicable law or regulation.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Third‑Party Content and Other Users
              </h2>
              <p>
                The Service may display links to third‑party websites, content, advertisements or other resources 
                ("Third‑Party Ads"). We do not control or endorse any Third‑Party Ads and are not responsible for 
                their content. Your interactions with third‑party websites or resources are solely between you 
                and the third party. We will not be liable for any loss or damage arising from your interactions 
                with Third‑Party Ads.
              </p>
              <p className="mt-4">
                Other users are solely responsible for their own User Content. We make no guarantees regarding the 
                accuracy, quality or suitability of any User Content.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. Disclaimer of Warranties
              </h2>
              <p>
                The Service and all content provided through it are offered "as is" and "as available" without 
                warranties of any kind, whether express or implied. We expressly disclaim any warranties of 
                title, non‑infringement, merchantability, fitness for a particular purpose, or that the Service 
                will be secure, accurate, timely, uninterrupted or error‑free. You use the Service at your 
                own risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Limitation of Liability
              </h2>
              <p>
                To the maximum extent permitted by law, we and our affiliates will not be liable to you for any 
                indirect, incidental, special, consequential or punitive damages, or for any lost profits or loss 
                of data, arising out of or in connection with your use of, or inability to use, the Service. 
                In no event shall our aggregate liability to you for all claims exceed the amounts you have paid 
                us for access to and use of the Service.
              </p>
              <p className="mt-4">
                Some jurisdictions do not allow certain limitations of liability; in such cases, our liability 
                will be limited to the extent permitted by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. Indemnity
              </h2>
              <p>
                You agree to indemnify, defend and hold harmless the Company and its affiliates, officers, directors, 
                employees and agents from and against all claims, liabilities, damages, losses and expenses (including 
                reasonable legal fees) arising out of or in any way connected with your use of the Service, your User 
                Content or your violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                11. International Use
              </h2>
              <p>
                Access to the Service may not be legal or appropriate in certain jurisdictions. You access the Service 
                on your own initiative and are responsible for compliance with all local laws. We make no 
                representation that the Service is available or suitable for use in your jurisdiction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                12. Dispute Resolution and Arbitration
              </h2>
              <p className="mb-3">
                <strong>Class action waiver.</strong> Except where prohibited by law, you agree that any dispute 
                arising out of these Terms or the Service will be resolved through final and binding arbitration 
                on an individual basis. You waive any right to participate in a class action or representative 
                proceeding.
              </p>
              <p className="mb-3">
                <strong>Arbitration procedure.</strong> A party seeking arbitration must first provide written notice 
                of the dispute to the other party at the address below. If the parties cannot resolve the dispute 
                within 30 days, either party may commence arbitration with a recognised arbitration provider. 
                The arbitration will be conducted in English, and the arbitrator may award the same damages and relief 
                as a court. The arbitrator's decision will be final and binding.
              </p>
              <p>
                If you do not wish to resolve disputes by arbitration, you may opt out within 30 days of first agreeing 
                to these Terms by sending written notice to the address below. This arbitration agreement will survive 
                termination of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                13. Governing Law
              </h2>
              <p>
                These Terms are governed by and construed in accordance with the laws of Australia, without regard to 
                its conflict of law principles. You submit to the non‑exclusive jurisdiction of the courts of New South 
                Wales for any action that is not subject to arbitration.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                14. Changes to Terms
              </h2>
              <p>
                We may amend these Terms from time to time at our discretion. When we make changes, we will update the 
                "Effective date" and may notify you via the Service or by email. Changes are effective when posted. 
                Your continued use of the Service constitutes your acceptance of the revised Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                15. Contact Information
              </h2>
              <p className="mb-3">
                If you have questions about these Terms, purchases or refunds, please contact:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:ayupathai@outlook.com" className="text-orange-600 hover:underline">
                    ayupathai@outlook.com
                  </a>
                </li>
                <li>
                  <strong>Address:</strong> 37 Fairfield Crescent, Diggers Rest, VIC 3427 Australia
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </Background>
  );
}
